import { useState, useEffect, useMemo } from "react";
import { useForm, FormProvider, useWatch, useFieldArray, type SubmitHandler } from "react-hook-form";
import {
  useCreateStockIn,
  useUpdateStockIn,
  useGetStockIn,
  getListStockInQueryKey,
  getGetStockInQueryKey,
  getListInventoryItemsQueryKey,
  useListChargeTypes,
  useListInventoryItems,
  useListStockInOrders,
  getListStockInOrdersQueryKey,
  useGetStockInOrder,
  getGetStockInOrderQueryKey,
  type StockInOrder,
} from "@workspace/api-client-react";
import { StockInOrderDeliveryForm } from "./stock-in-order-delivery-form";
import { useQueryClient } from "@tanstack/react-query";
import { Info, Loader2, Upload, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Separator } from "@/components/ui/separator";
import { Switch } from "@/components/ui/switch";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useToast } from "@/hooks/use-toast";
import { StockInLineItemsTable } from "./stock-in-line-items-table";
import { VendorCombobox } from "./vendor-combobox";
import { stkInId } from "@/lib/display-id";
import { uploadInvoice, uploadPaymentProof, type UploadResult } from "@/lib/upload";

// 2026-05-30: Quick_Commerce_Purchase and Direct_Purchase removed from the
// operator-facing dropdown — Purchase + a PO covers both flows. The enum
// stays in the server schema so existing rows still validate.
const TX_TYPES = ["Purchase","Opening_Stock","Member_Return","Transfer_In","Sample","Replacement"] as const;
const QUALITY_STATUSES = ["Passed","Passed_With_Notes","Partial_Acceptance","Rejected"] as const;
const PAYMENT_MODES = ["UPI","Card","Cash","Net_Banking","Auto_Debit"] as const;
// 2026-05-21: payment transaction details.
const PAID_BY = ["Company","Personal"] as const;
const PAYMENT_METHODS = ["Cash","UPI","Bank_Transfer","Debit_Card","Credit_Card","Wallet","Cheque"] as const;
// Wallet providers shown when Payment Method = Wallet.
const WALLET_PROVIDERS = ["Cred Flash","Amazon Flash"] as const;

interface StockInFormProps {
  mode: "create" | "edit" | "edit-stage-2";
  stockInId?: number | null;
  /** When the dialog is opened from the Internal Refills sub-tab, the form pre-fills transactionType=Transfer_In. */
  initialTransactionType?: string;
  onSaved: () => void;
  onDiscard: () => void;
  /**
   * Fired when the operator picks a Purchase Order while transactionType =
   * 'Purchase'. The parent should close this sheet and open the Record
   * Delivery dialog pre-selected to the given PO. Optional — falls back to a
   * helper banner if the parent doesn't wire it.
   */
  onPickPurchaseOrder?: (orderId: number) => void;
}

const STORAGE_LOCATIONS = ["Pantry", "Store_Room", "IT_Rack", "Cabin_Store", "Reception", "Office_Floor"] as const;

// Form values are validated server-side via Zod-generated route handlers; the UI form is a flat record
// plus a lineItems array. We use react-hook-form's untyped variant for flexibility across the 5 sections
// (Stage 1 vs Stage 2 fields differ).
type FormValues = Record<string, unknown> & { lineItems: Array<Record<string, unknown>> };

export function StockInForm({ mode, stockInId, initialTransactionType, onSaved, onDiscard }: StockInFormProps) {
  // When type=Purchase and the operator picks a PO, we stop rendering the
  // free-form Stock-In form and inline-mount the PO-linked delivery form
  // instead — single CTA, single sheet, no parallel dialog.
  const [linkedOrderId, setLinkedOrderId] = useState<number | null>(null);
  const linkedOrderQuery = useGetStockInOrder(linkedOrderId ?? 0, {
    query: {
      enabled: linkedOrderId != null && linkedOrderId > 0,
      queryKey: getGetStockInOrderQueryKey(linkedOrderId ?? 0),
    },
  });
  if (linkedOrderId != null && linkedOrderQuery.data) {
    return (
      <div className="flex flex-col gap-3 p-6">
        <div className="flex items-center justify-between rounded-md border bg-muted/30 px-3 py-2 text-sm">
          <span>
            Recording delivery against{" "}
            <span className="font-mono font-medium">
              {linkedOrderQuery.data.orderId ?? `ORD-${String(linkedOrderQuery.data.id).padStart(3, "0")}`}
            </span>
          </span>
          <Button type="button" variant="ghost" size="sm" onClick={() => setLinkedOrderId(null)}>
            Change PO
          </Button>
        </div>
        <StockInOrderDeliveryForm
          order={linkedOrderQuery.data}
          onSaved={onSaved}
          onDiscard={onDiscard}
        />
      </div>
    );
  }
  if (linkedOrderId != null) {
    return (
      <div className="flex items-center gap-2 p-6 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" />
        Loading order…
      </div>
    );
  }
  return (
    <StockInFormImpl
      mode={mode}
      stockInId={stockInId}
      initialTransactionType={initialTransactionType}
      onSaved={onSaved}
      onDiscard={onDiscard}
      onPickPurchaseOrder={(id) => setLinkedOrderId(id)}
    />
  );
}

function StockInFormImpl({ mode, stockInId, initialTransactionType, onSaved, onDiscard, onPickPurchaseOrder }: StockInFormProps) {
  const { toast } = useToast();
  const qc = useQueryClient();
  const { data: existing } = useGetStockIn(stockInId ?? 0, { query: { enabled: !!stockInId, queryKey: getGetStockInQueryKey(stockInId ?? 0) } });
  const createMut = useCreateStockIn({
    mutation: {
      onSuccess: () => {
        qc.invalidateQueries({ queryKey: getListStockInQueryKey() });
        // 2026-05-20: stock-in increments item current_stock server-side — refresh the
        // items list so the Items tab shows the new quantity (was showing stale stock).
        qc.invalidateQueries({ queryKey: getListInventoryItemsQueryKey() });
        toast({ title: "Stock In saved." });
        onSaved();
      },
      onError: () => toast({ title: "Something went wrong. Please try again.", variant: "destructive" }),
    },
  });
  const updateMut = useUpdateStockIn({
    mutation: {
      onSuccess: () => {
        qc.invalidateQueries({ queryKey: getListStockInQueryKey() });
        qc.invalidateQueries({ queryKey: getListInventoryItemsQueryKey() });
        toast({ title: mode === "edit-stage-2" ? "Stock In reconciled." : "Stock In updated." });
        onSaved();
      },
      onError: () => toast({ title: "Something went wrong. Please try again.", variant: "destructive" }),
    },
  });

  // ISO datetime / Date → "YYYY-MM-DD" for <input type=date>. Empty/invalid → "".
  function toDateInput(v: unknown): string {
    if (!v) return "";
    if (typeof v === "string") return v.slice(0, 10);
    if (v instanceof Date) return v.toISOString().slice(0, 10);
    return "";
  }

  function buildDefaults(): FormValues {
    if (!existing) {
      return {
        transactionDate: new Date().toISOString().slice(0, 10),
        transactionType: initialTransactionType ?? "Purchase",
        lineItems: [],
      };
    }
    // 2026-05-20: back-derive gstRate (%) from the stored gstAmount so the GST %
    // dropdown shows the right value on edit (server stores amount, not rate).
    const ex = existing as unknown as FormValues;
    const lineItems = Array.isArray(ex.lineItems)
      ? ex.lineItems.map((li) => {
          const qty = Number(li.quantityReceived) || 0;
          const price = Number(li.unitPriceActual) || 0;
          const amt = Number(li.gstAmount) || 0;
          const base = qty * price;
          const rawRate = base > 0 ? (amt / base) * 100 : 0;
          const rate = [0, 5, 12, 18, 28, 40].reduce((best, r) =>
            Math.abs(r - rawRate) < Math.abs(best - rawRate) ? r : best, 0);
          return { ...li, gstRate: rate, expiryDate: toDateInput(li.expiryDate) };
        })
      : ex.lineItems;
    // 2026-05-22: hydrate charge lines (label + amount + gstRate) for editing.
    const exCharges = (ex as Record<string, unknown>)["charges"];
    const charges = Array.isArray(exCharges)
      ? (exCharges as Array<Record<string, unknown>>).map((c) => ({
          label: String(c["label"] ?? ""),
          amount: Number(c["amount"]) || 0,
          gstRate: c["gstRate"] != null ? Number(c["gstRate"]) : 0,
        }))
      : [];
    return {
      ...ex,
      // 2026-05-20: convert ISO datetimes to YYYY-MM-DD so the date inputs populate on edit.
      transactionDate: toDateInput(ex.transactionDate),
      receivedAt: toDateInput(ex.receivedAt),
      invoiceDate: toDateInput(ex.invoiceDate),
      lineItems,
      charges,
    } as FormValues;
  }

  const form = useForm<FormValues>({
    defaultValues: buildDefaults(),
  });

  // 2026-05-20: useForm only applies defaultValues on first mount, but `existing`
  // loads async after mount in edit mode — so the form stayed empty (dates blank,
  // Financial Summary wrong). Reset the form once the existing record arrives.
  useEffect(() => {
    if (existing) form.reset(buildDefaults());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [existing]);

  // Phase 2.1 polish B — Internal Refill mode swaps several Section A/B/D/E fields for from/to location.
  const transactionType = useWatch({ control: form.control, name: "transactionType" }) as string | undefined;
  const isInternalRefill = transactionType === "Transfer_In";
  // R1.5 (migration 0047): per-receipt payment fields retired — payment lives on the PO.
  // 2026-05-22: additional charge lines + the charge-type master that drives the picker.
  const chargesArray = useFieldArray({ control: form.control as never, name: "charges" });
  const { data: chargeTypes = [] } = useListChargeTypes({ status: "Active" });

  // 2026-05-22: are the entered unit prices inclusive of GST? When yes, the embedded
  // GST is split out of each price; when no, GST is added on top. UI-only (not persisted).
  const priceInclusiveGst = (useWatch({ control: form.control, name: "priceInclusiveGst" }) as boolean | undefined) ?? false;

  // 2026-05-20: auto-compute Financial Summary from line items (read-only display).
  // Subtotal is always the pre-GST base; Total GST the GST on it; Grand Total the sum.
  // For GST-inclusive entry, base = price ÷ (1 + rate/100) and GST is the remainder.
  const watchedLineItems = (useWatch({ control: form.control, name: "lineItems" }) as Array<Record<string, unknown>> | undefined) ?? [];

  // ── Completeness gate for the final "Save Stock-In" button ──
  // "Save Draft" is always available; "Save Stock-In" enables only when everything's
  // filled: expiry on FOOD/perishable line items (item has a shelf life), quality check,
  // payment status, and all applicable payment transaction details.
  const { data: itemsForShelfLife = [] } = useListInventoryItems({ status: "Active" });
  const shelfLifeById = useMemo(() => {
    const m = new Map<number, number>();
    for (const it of itemsForShelfLife as Array<{ id: number; shelfLifeDays?: number | null }>) {
      m.set(it.id, Number(it.shelfLifeDays ?? 0));
    }
    return m;
  }, [itemsForShelfLife]);
  const wQuality = useWatch({ control: form.control, name: "qualityStatus" }) as string | undefined;
  const filledStr = (v: unknown) => v != null && String(v).trim() !== "";
  const missingForFinal: string[] = [];
  if (watchedLineItems.length === 0) missingForFinal.push("at least one line item");
  // 1. Expiry on food / perishable line items (item has a shelf life)
  if (watchedLineItems.some((li) => (shelfLifeById.get(Number(li?.["itemId"])) ?? 0) > 0 && !filledStr(li?.["expiryDate"]))) {
    missingForFinal.push("expiry date on food items");
  }
  // 2. Quality check
  if (!filledStr(wQuality)) missingForFinal.push("quality check");
  // 3. Phase 58 Plan 13 (LOC-01): location required on receive (CONTEXT.md) —
  // enforced client-side even though the API still tolerates an absent
  // locationId for older clients. Not gated for Internal Refill, which
  // carries its own From/To location pair instead.
  if (!isInternalRefill && watchedLineItems.some((li) => !li?.["locationId"])) {
    missingForFinal.push("receiving location on every line");
  }
  // R1.5 (migration 0047): payment-detail completeness gating retired — payment
  // is recorded on the parent PO, not per receipt.
  const isComplete = missingForFinal.length === 0;

  const round2 = (n: number) => Math.round(n * 100) / 100;
  // Match the submit-path rounding: baseUnitPrice is rounded to 2 decimals per
  // line BEFORE the qty × base × rate computation, so the display totals equal
  // the totals we're about to persist (see onSubmit line ~355). Without this,
  // multi-line receipts diverge from stored subtotal/grandTotal by a few paisa
  // because SUM(qty × unrounded_base) ≠ SUM(qty × rounded_base) (STK-IN-098:
  // 6-paisa subtotal drift over 15 lines).
  const lineBaseGst = (li: Record<string, unknown>) => {
    const qty = Number(li?.["quantityReceived"]) || 0;
    const price = Number(li?.["unitPriceActual"]) || 0;
    const rate = Number(li?.["gstRate"]) || 0;
    const unitBaseRaw = priceInclusiveGst ? price / (1 + rate / 100) : price;
    const baseUnitPrice = Math.round(unitBaseRaw * 100) / 100;
    const base = qty * baseUnitPrice;
    const gst = Math.round(qty * baseUnitPrice * rate) / 100;
    return { base, gst };
  };
  const itemsBase = round2(watchedLineItems.reduce((s, li) => s + lineBaseGst(li).base, 0));
  const itemsGst = round2(watchedLineItems.reduce((s, li) => s + lineBaseGst(li).gst, 0));
  // 2026-05-22: additional charges (delivery, handling, platform fee, …), each with GST.
  const watchedCharges = (useWatch({ control: form.control, name: "charges" }) as Array<Record<string, unknown>> | undefined) ?? [];
  const chargesBase = round2(watchedCharges.reduce((s, c) => s + (Number(c?.["amount"]) || 0), 0));
  const chargesGst = round2(
    watchedCharges.reduce((s, c) => s + ((Number(c?.["amount"]) || 0) * (Number(c?.["gstRate"]) || 0)) / 100, 0),
  );
  // Subtotal shows the item base; charges are surfaced separately but fold into GST + grand total.
  const computedSubtotal = itemsBase;
  const computedTotalGst = round2(itemsGst + chargesGst);
  const computedGrandTotal = round2(itemsBase + chargesBase + itemsGst + chargesGst);

  useEffect(() => {
    form.setValue("subtotal", computedSubtotal);
    form.setValue("totalGst", computedTotalGst);
    form.setValue("grandTotal", computedGrandTotal);
  }, [computedSubtotal, computedTotalGst, computedGrandTotal]); // eslint-disable-line react-hooks/exhaustive-deps

  // Convert "YYYY-MM-DD" date inputs into full ISO datetimes the server expects.
  // Phase 2.1 polish L — date-only inputs replace datetime-local; we anchor at midnight UTC.
  // 2026-05-20: empty/blank dates become null (a blank <input type=date> yields "",
  // which the server's Zod date validator rejects — was breaking Stock-In on a blank
  // line-item Expiry Date).
  function toIsoIfDate(v: unknown): unknown {
    if (typeof v === "string") {
      if (v.trim() === "") return null;
      if (/^\d{4}-\d{2}-\d{2}$/.test(v)) return new Date(v + "T00:00:00.000Z").toISOString();
    }
    return v;
  }

  const onSubmit: SubmitHandler<FormValues> = async (data) => {
    // Completeness is gated by the "Save Stock-In" button's enabled state
    // (expiry on food items, quality check, payment status, payment details).
    // "Save Draft" saves partial data; both paths persist the same record.
    // Numeric header fields arrive as strings from the DB (numeric(12,2) → "183.00").
    // The spread below would PATCH them as strings, which fails UpdateStockInBody's
    // number validation. Coerce every numeric header field to number-or-null, and send
    // the freshly COMPUTED financial summary for subtotal / totalGst / grandTotal.
    const numOrNull = (v: unknown): number | null =>
      v == null || v === "" || Number.isNaN(Number(v)) ? null : Number(v);
    // 2026-07-14 — the Quality Status <Select> emits "" when the operator
    // hasn't picked one, but UpdateStockInBody Zod is a strict enum with no
    // empty-string variant. Coerce blank to null so the server sees "unset"
    // instead of an invalid enum value.
    const strOrNull = (v: unknown): string | null =>
      v == null || (typeof v === "string" && v.trim() === "") ? null : String(v);
    const normalized: FormValues = {
      ...data,
      transactionDate: toIsoIfDate(data.transactionDate),
      // 2026-05-21: Received At merged into Transaction Date — keep them in sync.
      receivedAt: toIsoIfDate(data.transactionDate),
      invoiceDate: toIsoIfDate(data.invoiceDate),
      qualityStatus: strOrNull((data as { qualityStatus?: unknown }).qualityStatus),
      subtotal: computedSubtotal,
      totalGst: computedTotalGst,
      grandTotal: computedGrandTotal,
      deliveryCharges: numOrNull(data.deliveryCharges),
      otherCharges: numOrNull(data.otherCharges),
      discount: numOrNull(data.discount),
      acceptedQuantity: numOrNull(data.acceptedQuantity),
      rejectedQuantity: numOrNull(data.rejectedQuantity),
      estimatedAmount: numOrNull(data.estimatedAmount),
      productSubtotal: numOrNull(data.productSubtotal),
      deliveryCharge: numOrNull(data.deliveryCharge),
      handlingFee: numOrNull(data.handlingFee),
      surgeFee: numOrNull(data.surgeFee),
      tip: numOrNull(data.tip),
      discountApplied: numOrNull(data.discountApplied),
      gstAmount: numOrNull(data.gstAmount),
      finalPaidAmount: numOrNull(data.finalPaidAmount),
      varianceVsEstimate: numOrNull(data.varianceVsEstimate),
      // 2026-05-20: normalize per-line-item dates (expiryDate → null when blank) and
      // compute gstAmount from the operator-entered GST % (qty × unit price × rate / 100).
      // gstRate is UI-only — stripped from the payload (server stores gstAmount).
      lineItems: Array.isArray((data as { lineItems?: unknown[] }).lineItems)
        ? (data as { lineItems: Record<string, unknown>[] }).lineItems.map((li) => {
            // Send only the schema fields with numeric types. Server-only fields
            // (id, stockInId, spaceId, variantId, quantityVariance, createdAt) and the
            // server-recomputed lineTotal are dropped — they arrive as strings from the
            // DB and would fail the line-item validation (lineTotal expects a number).
            const qty = Number(li.quantityReceived) || 0;
            const price = Number(li.unitPriceActual) || 0;
            const rate = Number(li.gstRate) || 0;
            // 2026-05-22: store unitPriceActual as the pre-GST base. When the operator
            // entered a GST-inclusive price, split out the embedded GST; otherwise add it.
            const inclusive = Boolean((data as { priceInclusiveGst?: boolean }).priceInclusiveGst);
            const unitBase = inclusive ? price / (1 + rate / 100) : price;
            const baseUnitPrice = Math.round(unitBase * 100) / 100;
            const gstAmount = Math.round(qty * baseUnitPrice * rate) / 100;
            // 2026-06-30: every receipt credits the full quantityReceived to current
            // stock, regardless of transaction date. (The old back-dated "still in
            // stock today" mode was removed.) quantityRemaining = null tells the server
            // to add the full received qty.
            return {
              itemId: Number(li.itemId),
              quantityReceived: qty,
              quantityRemaining: null,
              quantityOrdered:
                li.quantityOrdered != null && li.quantityOrdered !== "" ? Number(li.quantityOrdered) : undefined,
              unitPriceActual: baseUnitPrice,
              gstAmount,
              batchNumber: (li.batchNumber as string) || undefined,
              expiryDate: toIsoIfDate(li.expiryDate),
              storageLocation: (li.storageLocation as string) || undefined,
              // Phase 58 Plan 13 (LOC-01): per-line "Receive into" location.
              // Read directly off the raw body server-side (58-05's convention —
              // not yet in the orval-generated line-item schema), so this key
              // rides along even though CreateStockInLineItem's TS type doesn't
              // declare it.
              locationId: li.locationId != null && li.locationId !== "" ? Number(li.locationId) : undefined,
            };
          })
        : (data as { lineItems?: unknown }).lineItems,
      // 2026-05-22: charge lines — compute each line's GST amount (amount × rate / 100).
      charges: Array.isArray((data as Record<string, unknown>)["charges"])
        ? ((data as Record<string, unknown>)["charges"] as Record<string, unknown>[])
            .filter((c) => (c["label"] as string)?.trim() && Number(c["amount"]) > 0)
            .map((c) => {
              const amount = Number(c["amount"]) || 0;
              const rate = Number(c["gstRate"]) || 0;
              return { label: String(c["label"]).trim(), amount, gstRate: rate, gstAmount: Math.round(amount * rate) / 100 };
            })
        : [],
    } as FormValues;
    if (mode === "create") {
      await createMut.mutateAsync({ data: normalized as unknown as Parameters<typeof createMut.mutateAsync>[0]["data"] });
    } else if (stockInId) {
      await updateMut.mutateAsync({ id: stockInId, data: normalized as unknown as Parameters<typeof updateMut.mutateAsync>[0]["data"] });
    }
  };

  const stage2 = mode === "edit-stage-2";

  // R1 (migration 0046): per-receipt paymentStatus retired. Stock-in receipts
  // are no longer locked by their own status; lifecycle gating moves to the PO.
  const locked = false;
  async function handleReopen() { /* no-op — kept to preserve callsites until UI rewrite */ }
  const title = mode === "create"
    ? "Add Stock In"
    : mode === "edit-stage-2"
    ? `Reconcile ${stockInId ? stkInId(stockInId) : ""} — Quick Commerce`
    : `Edit ${stockInId ? stkInId(stockInId) : ""}`;

  return (
    <div className="flex flex-col gap-6 p-6">
      <header><h2 className="text-2xl font-bold">{title}</h2></header>
      <Form {...form}>
        <FormProvider {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col gap-8">
            {locked && (
              <div className="rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900">
                🔒 This stock-in is <b>Paid</b> and locked — line items and amounts can&rsquo;t be changed.
                Use <b>Reopen for editing</b> below to set it back to Pending.
              </div>
            )}
            {/* 2026-05-20: fieldset[disabled] freezes every control inside when Paid. */}
            <fieldset disabled={locked} className="contents">
            {/* Section A — Header */}
            <section>
              <h3 className="text-sm font-semibold mb-3">Header</h3>
              <div className="grid grid-cols-2 gap-4">
                <FormField name="transactionDate" control={form.control} render={({ field }) => (
                  <FormItem>
                    <FormLabel>Transaction Date *</FormLabel>
                    <FormControl><Input type="date" className="max-w-[220px]" disabled={stage2} value={(field.value as string) ?? ""} onChange={field.onChange} onBlur={field.onBlur} name={field.name} ref={field.ref} /></FormControl>
                    <FormMessage />
                  </FormItem>
                )} />
                <FormField name="transactionType" control={form.control} render={({ field }) => (
                  <FormItem>
                    <FormLabel className="flex items-center gap-1">
                      Type *
                      <Popover>
                        <PopoverTrigger asChild>
                          <button type="button" className="text-muted-foreground hover:text-foreground" aria-label="When to use which type">
                            <Info className="h-3.5 w-3.5" />
                          </button>
                        </PopoverTrigger>
                        <PopoverContent className="w-[420px] text-xs space-y-1.5" align="start">
                          <p className="font-medium text-sm">When to use which</p>
                          <p><b>Purchase</b> — regular vendor; pick the PO to pre-load line items into Record Delivery</p>
                          <p><b>Opening Stock</b> — initial counts or annual reconciliation</p>
                          <p><b>Member Return</b> — member returns an item (locker key, bottle…)</p>
                          <p><b>Transfer In</b> — internal storage → floor refill (use the Internal Refills sub-tab)</p>
                          <p><b>Sample</b> — vendor sample, no payment</p>
                          <p><b>Replacement</b> — vendor replaces defective unit, no new payment</p>
                          <p><b>Daily Purchase</b> — milk/dairy local-shop pickup (use the Daily Pickups sub-tab)</p>
                        </PopoverContent>
                      </Popover>
                    </FormLabel>
                    <Select disabled={stage2} value={(field.value as string) ?? ""} onValueChange={field.onChange}>
                      <FormControl><SelectTrigger><SelectValue /></SelectTrigger></FormControl>
                      <SelectContent>{TX_TYPES.map((t) => <SelectItem key={t} value={t}>{t.replace(/_/g, " ")}</SelectItem>)}</SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )} />
                {/* 2026-05-30: when transactionType=Purchase, surface a PO picker
                    spanning the row so the operator can jump straight into the
                    Record Delivery flow for that PO. Lists POs that are Placed
                    or Partially_Delivered AND have items still outstanding. */}
                {transactionType === "Purchase" && mode === "create" && (
                  <div className="col-span-2">
                    <PurchaseOrderPickerInline
                      onPick={(orderId) => onPickPurchaseOrder?.(orderId)}
                    />
                  </div>
                )}
                {/* 2026-05-21: "Received At" merged into Transaction Date (single date).
                    On submit receivedAt is set = transactionDate, so the column still gets a value. */}
                <FormField name="referenceNumber" control={form.control} render={({ field }) => (
                  <FormItem>
                    <FormLabel>Reference Number</FormLabel>
                    <FormControl><Input disabled={stage2} value={(field.value as string) ?? ""} onChange={field.onChange} /></FormControl>
                  </FormItem>
                )} />
              </div>
            </section>
            <Separator />

            {/* Phase 2.1 polish B — Internal Refill: from/to location (Transfer_In only) */}
            {isInternalRefill ? (
              <>
                <section>
                  <h3 className="text-sm font-semibold mb-3">Refill Locations</h3>
                  <p className="text-xs text-muted-foreground mb-3">Supervisor refill from central storage to operational point. No vendor / invoice / quality check needed.</p>
                  <div className="grid grid-cols-2 gap-4">
                    <FormField name="fromLocation" control={form.control} render={({ field }) => (
                      <FormItem>
                        <FormLabel>From <span className="text-destructive">*</span></FormLabel>
                        <Select disabled={stage2} value={(field.value as string) ?? ""} onValueChange={field.onChange}>
                          <FormControl><SelectTrigger><SelectValue placeholder="Source location" /></SelectTrigger></FormControl>
                          <SelectContent>{STORAGE_LOCATIONS.map((s) => <SelectItem key={s} value={s}>{s.replace(/_/g, " ")}</SelectItem>)}</SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )} />
                    <FormField name="toLocation" control={form.control} render={({ field }) => (
                      <FormItem>
                        <FormLabel>To <span className="text-destructive">*</span></FormLabel>
                        <Select disabled={stage2} value={(field.value as string) ?? ""} onValueChange={field.onChange}>
                          <FormControl><SelectTrigger><SelectValue placeholder="Destination location" /></SelectTrigger></FormControl>
                          <SelectContent>{STORAGE_LOCATIONS.map((s) => <SelectItem key={s} value={s}>{s.replace(/_/g, " ")}</SelectItem>)}</SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )} />
                  </div>
                </section>
                <Separator />
              </>
            ) : null}

            {/* Section B — Vendor & Order Reference (External Receipts only) */}
            {!isInternalRefill ? (
            <>
            <section>
              <h3 className="text-sm font-semibold mb-3">Vendor & Order Reference</h3>
              <div className="grid grid-cols-2 gap-4">
                <FormField name="vendorId" control={form.control} render={({ field }) => (
                  <FormItem>
                    <FormLabel>Vendor</FormLabel>
                    <FormControl>
                      <VendorCombobox
                        value={(field.value as number | null) ?? null}
                        onChange={(id) => field.onChange(id)}
                      />
                    </FormControl>
                  </FormItem>
                )} />
                {/* Free-text PO Number field — hidden when type=Purchase
                    because that flow uses the PO picker above (linking to
                    stock_in_order_id). Other transaction types may still
                    want to record an external PO reference. */}
                {transactionType !== "Purchase" && (
                  <FormField name="purchaseOrderId" control={form.control} render={({ field }) => (
                    <FormItem>
                      <FormLabel>PO Number</FormLabel>
                      <FormControl><Input disabled={stage2} placeholder="Optional" value={(field.value as string) ?? ""} onChange={field.onChange} /></FormControl>
                    </FormItem>
                  )} />
                )}
                <FormField name="invoiceNumber" control={form.control} render={({ field }) => (
                  <FormItem>
                    <FormLabel>Invoice Number</FormLabel>
                    <FormControl><Input disabled={stage2} value={(field.value as string) ?? ""} onChange={field.onChange} /></FormControl>
                  </FormItem>
                )} />
                <FormField name="invoiceDate" control={form.control} render={({ field }) => (
                  <FormItem>
                    <FormLabel>Invoice Date</FormLabel>
                    <FormControl><Input type="date" className="max-w-[220px]" disabled={stage2} value={(field.value as string) ?? ""} onChange={field.onChange} /></FormControl>
                  </FormItem>
                )} />
                <FormField name="invoicePdfUrl" control={form.control} render={({ field }) => (
                  <FormItem className="col-span-2">
                    <FormLabel>Upload Invoice (PDF / Image)</FormLabel>
                    <FormControl>
                      <InvoiceUploader
                        value={(field.value as string | null | undefined) ?? null}
                        onChange={(url) => field.onChange(url)}
                        disabled={stage2}
                      />
                    </FormControl>
                  </FormItem>
                )} />
                <FormField name="gstInputClaimed" control={form.control} render={({ field }) => (
                  <FormItem className="col-span-2 flex items-center justify-between rounded-md border p-3">
                    <div>
                      <FormLabel className="font-medium">Claimed input GST credit</FormLabel>
                      <p className="text-xs text-muted-foreground">Tick when you've claimed this invoice's GST in your filing</p>
                    </div>
                    <FormControl>
                      <Switch
                        checked={Boolean(field.value)}
                        onCheckedChange={(c) => field.onChange(c)}
                        disabled={stage2}
                      />
                    </FormControl>
                  </FormItem>
                )} />
              </div>
            </section>
            <Separator />
            </>
            ) : null}

            {/* Section C — Line Items */}
            <section>
              <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
                <h3 className="text-sm font-semibold">Line Items</h3>
                {/* 2026-05-22: GST-inclusive vs exclusive entry. Exclusive adds GST on top;
                    inclusive splits the embedded GST out of the entered price. */}
                <FormField name="priceInclusiveGst" control={form.control} render={({ field }) => (
                  <div className="flex items-center gap-2">
                    <span className={`text-xs ${field.value ? "text-muted-foreground" : "font-medium"}`}>Price excl. GST</span>
                    <Switch
                      checked={Boolean(field.value)}
                      onCheckedChange={(c) => field.onChange(c)}
                      disabled={stage2}
                      aria-label="Toggle whether unit prices include GST"
                    />
                    <span className={`text-xs ${field.value ? "font-medium" : "text-muted-foreground"}`}>Price incl. GST</span>
                  </div>
                )} />
              </div>
              {/* Phase 58 Plan 13 (LOC-01): "Receive into" is hidden for Internal Refill
                  (Transfer_In) — that flow already carries its own From/To location pair
                  above (the pre-58 free-text storage_location fields); this per-line
                  stock_locations picker is for real external receipts. */}
              <StockInLineItemsTable
                control={form.control as never}
                disabled={stage2}
                priceInclusiveGst={priceInclusiveGst}
                showLocationPicker={!isInternalRefill}
              />
            </section>
            <Separator />

            {/* Section C2 — Additional Charges (2026-05-22): delivery, handling, platform fee, … */}
            <section>
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold">Additional Charges</h3>
                {!stage2 && (
                  <Button type="button" size="sm" variant="outline" onClick={() => chargesArray.append({ label: "", amount: 0, gstRate: 18 } as never)}>
                    + Add charge
                  </Button>
                )}
              </div>
              {chargesArray.fields.length === 0 ? (
                <p className="text-xs text-muted-foreground rounded-md border border-dashed px-3 py-2">
                  No extra charges. Add delivery / handling / platform fees here — manage the list in Inventory → Charges.
                </p>
              ) : (
                <div className="space-y-2">
                  {chargesArray.fields.map((field, i) => {
                    const amount = Number(form.watch(`charges.${i}.amount` as never)) || 0;
                    const rate = Number(form.watch(`charges.${i}.gstRate` as never)) || 0;
                    const gst = Math.round(amount * rate) / 100;
                    return (
                      <div key={field.id} className="grid grid-cols-[1fr_110px_90px_auto] gap-2 items-end rounded-md border bg-card p-2">
                        <div>
                          <label className="text-[11px] text-muted-foreground mb-1 block">Charge</label>
                          <Select
                            value={(form.watch(`charges.${i}.label` as never) as unknown as string) || ""}
                            onValueChange={(v) => {
                              form.setValue(`charges.${i}.label` as never, v as never);
                              const t = chargeTypes.find((ct) => ct.name === v);
                              if (t?.defaultGstRate != null) form.setValue(`charges.${i}.gstRate` as never, Number(t.defaultGstRate) as never);
                            }}
                            disabled={stage2}
                          >
                            <SelectTrigger><SelectValue placeholder="Select charge…" /></SelectTrigger>
                            <SelectContent>
                              {chargeTypes.map((ct) => <SelectItem key={ct.id} value={ct.name}>{ct.name}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <label className="text-[11px] text-muted-foreground mb-1 block">Amount ₹</label>
                          <Input
                            type="number"
                            step="0.01"
                            disabled={stage2}
                            className="font-mono tabular-nums"
                            value={(form.watch(`charges.${i}.amount` as never) as unknown as number | string) ?? ""}
                            onChange={(e) => form.setValue(`charges.${i}.amount` as never, (e.target.value === "" ? 0 : Number(e.target.value)) as never)}
                          />
                        </div>
                        <div>
                          <label className="text-[11px] text-muted-foreground mb-1 block">GST %</label>
                          <select
                            disabled={stage2}
                            className="flex h-10 w-full rounded-md border border-input bg-background px-2 py-2 text-sm font-mono tabular-nums ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                            value={String(rate)}
                            onChange={(e) => form.setValue(`charges.${i}.gstRate` as never, Number(e.target.value) as never)}
                          >
                            {[0, 5, 12, 18, 28, 40].map((r) => <option key={r} value={r}>{r}%</option>)}
                          </select>
                        </div>
                        <Button type="button" variant="ghost" size="sm" disabled={stage2} className="text-destructive hover:text-destructive h-9 w-9 p-0" onClick={() => chargesArray.remove(i)} aria-label="Remove charge">
                          <X className="h-4 w-4" />
                        </Button>
                        <p className="col-span-4 text-[11px] text-muted-foreground font-mono tabular-nums -mt-1">
                          GST ₹{gst.toFixed(2)} · line total ₹{(amount + gst).toFixed(2)}
                        </p>
                      </div>
                    );
                  })}
                </div>
              )}
            </section>
            <Separator />

            {/* Sections D + E — only render for external Receipts (skipped for Internal Refill) */}
            {!isInternalRefill ? (
            <>
            {/* Section D — Quality Check */}
            <section>
              <h3 className="text-sm font-semibold mb-3">Quality Check</h3>
              <div className="grid grid-cols-2 gap-4">
                <FormField name="qualityStatus" control={form.control} render={({ field }) => (
                  <FormItem>
                    <FormLabel>Quality Status</FormLabel>
                    <Select value={(field.value as string) ?? ""} onValueChange={field.onChange}>
                      <FormControl><SelectTrigger><SelectValue /></SelectTrigger></FormControl>
                      <SelectContent>{QUALITY_STATUSES.map((s) => <SelectItem key={s} value={s}>{s.replace(/_/g, " ")}</SelectItem>)}</SelectContent>
                    </Select>
                  </FormItem>
                )} />
                <FormField name="qualityNotes" control={form.control} render={({ field }) => (
                  <FormItem>
                    <FormLabel>Notes</FormLabel>
                    <FormControl><Textarea value={(field.value as string) ?? ""} onChange={field.onChange} /></FormControl>
                  </FormItem>
                )} />
              </div>
            </section>
            <Separator />

            {/* Section E — Financial Summary (Stage 2 fields editable in stage-2 mode) */}
            <section>
              <h3 className="text-sm font-semibold mb-3">Financial Summary</h3>
              <div className="grid grid-cols-3 gap-4">
                {stage2 ? (
                  <>
                    <FormField name="productSubtotal" control={form.control} render={({ field }) => (
                      <FormItem><FormLabel>Product Subtotal *</FormLabel><FormControl><Input type="number" step="0.01" value={(field.value as number | string) ?? ""} onChange={(e) => field.onChange(e.target.value === "" ? null : Number(e.target.value))} /></FormControl><FormMessage /></FormItem>
                    )} />
                    <FormField name="deliveryCharge" control={form.control} render={({ field }) => (
                      <FormItem><FormLabel>Delivery Charge</FormLabel><FormControl><Input type="number" step="0.01" value={(field.value as number | string) ?? ""} onChange={(e) => field.onChange(e.target.value === "" ? null : Number(e.target.value))} /></FormControl></FormItem>
                    )} />
                    <FormField name="handlingFee" control={form.control} render={({ field }) => (
                      <FormItem><FormLabel>Handling Fee</FormLabel><FormControl><Input type="number" step="0.01" value={(field.value as number | string) ?? ""} onChange={(e) => field.onChange(e.target.value === "" ? null : Number(e.target.value))} /></FormControl></FormItem>
                    )} />
                    <FormField name="surgeFee" control={form.control} render={({ field }) => (
                      <FormItem><FormLabel>Surge Fee</FormLabel><FormControl><Input type="number" step="0.01" value={(field.value as number | string) ?? ""} onChange={(e) => field.onChange(e.target.value === "" ? null : Number(e.target.value))} /></FormControl></FormItem>
                    )} />
                    <FormField name="tip" control={form.control} render={({ field }) => (
                      <FormItem><FormLabel>Tip</FormLabel><FormControl><Input type="number" step="0.01" value={(field.value as number | string) ?? ""} onChange={(e) => field.onChange(e.target.value === "" ? null : Number(e.target.value))} /></FormControl></FormItem>
                    )} />
                    <FormField name="discountApplied" control={form.control} render={({ field }) => (
                      <FormItem><FormLabel>Discount Applied</FormLabel><FormControl><Input type="number" step="0.01" value={(field.value as number | string) ?? ""} onChange={(e) => field.onChange(e.target.value === "" ? null : Number(e.target.value))} /></FormControl></FormItem>
                    )} />
                    <FormField name="finalPaidAmount" control={form.control} render={({ field }) => (
                      <FormItem><FormLabel>Final Paid *</FormLabel><FormControl><Input type="number" step="0.01" value={(field.value as number | string) ?? ""} onChange={(e) => field.onChange(e.target.value === "" ? null : Number(e.target.value))} /></FormControl></FormItem>
                    )} />
                    <FormField name="paymentMode" control={form.control} render={({ field }) => (
                      <FormItem>
                        <FormLabel>Payment Mode</FormLabel>
                        <Select value={(field.value as string) ?? ""} onValueChange={field.onChange}>
                          <FormControl><SelectTrigger><SelectValue /></SelectTrigger></FormControl>
                          <SelectContent>{PAYMENT_MODES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                        </Select>
                      </FormItem>
                    )} />
                    <FormField name="invoiceScreenshotUrl" control={form.control} render={({ field }) => (
                      <FormItem><FormLabel>Invoice Screenshot URL</FormLabel><FormControl><Input value={(field.value as string) ?? ""} onChange={field.onChange} /></FormControl></FormItem>
                    )} />
                  </>
                ) : (
                  <>
                    {/* 2026-05-20: disabled, auto-computed from line items. Plain <div>/<label>
                        markup — NOT FormItem/FormLabel, which require a FormField context and
                        throw "useFormField should be used within <FormField>" → white screen. */}
                    <div className="space-y-2">
                      <label className="text-sm font-medium leading-none">Subtotal (items)</label>
                      <Input disabled tabIndex={-1} className="font-mono tabular-nums bg-muted/60 cursor-not-allowed" value={`₹ ${computedSubtotal.toFixed(2)}`} readOnly />
                    </div>
                    {chargesBase > 0 && (
                      <div className="space-y-2">
                        <label className="text-sm font-medium leading-none">Charges</label>
                        <Input disabled tabIndex={-1} className="font-mono tabular-nums bg-muted/60 cursor-not-allowed" value={`₹ ${chargesBase.toFixed(2)}`} readOnly />
                      </div>
                    )}
                    <div className="space-y-2">
                      <label className="text-sm font-medium leading-none">Total GST</label>
                      <Input disabled tabIndex={-1} className="font-mono tabular-nums bg-muted/60 cursor-not-allowed" value={`₹ ${computedTotalGst.toFixed(2)}`} readOnly />
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-medium leading-none">Grand Total</label>
                      <Input disabled tabIndex={-1} className="font-mono tabular-nums bg-muted/60 cursor-not-allowed font-semibold" value={`₹ ${computedGrandTotal.toFixed(2)}`} readOnly />
                    </div>
                    {/* R1 (migration 0046): per-receipt Payment Status retired —
                        payment lives only on the parent PO now. */}
                  </>
                )}
              </div>
            </section>
            <Separator />

            {/* R1.5 (migration 0047): Payment Transaction Details section
                retired. Payment now lives on the parent PO (stock_in_orders).
                Operators record payment once on the order; deliveries inherit it. */}
            </>
            ) : null}
            </fieldset>

            {/* Surge/Deal banners are conditionally rendered by Quick Commerce tab on mutation response — Stage 2 polish hook */}

            <div className="flex flex-col items-end gap-2">
              {!stage2 && !locked && !isComplete && (
                <p className="text-xs text-muted-foreground text-right max-w-md">
                  Save Stock-In needs: {missingForFinal.join(", ")}. Use Save Draft to keep partial entry.
                </p>
              )}
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={onDiscard}>Discard changes</Button>
                {locked ? (
                  <Button type="button" variant="secondary" onClick={handleReopen} disabled={updateMut.isPending}>
                    {updateMut.isPending ? "Reopening…" : "Reopen for editing"}
                  </Button>
                ) : stage2 ? (
                  <Button type="submit" disabled={createMut.isPending || updateMut.isPending}>
                    {updateMut.isPending ? "Reconciling…" : "Reconcile (Stage 2)"}
                  </Button>
                ) : (
                  <>
                    <Button
                      type="button"
                      variant="secondary"
                      onClick={form.handleSubmit(onSubmit)}
                      disabled={createMut.isPending || updateMut.isPending}
                    >
                      {createMut.isPending || updateMut.isPending ? "Saving…" : "Save Draft"}
                    </Button>
                    <Button
                      type="button"
                      onClick={form.handleSubmit(onSubmit)}
                      disabled={!isComplete || createMut.isPending || updateMut.isPending}
                      title={isComplete ? undefined : `Missing: ${missingForFinal.join(", ")}`}
                    >
                      Save Stock In
                    </Button>
                  </>
                )}
              </div>
            </div>
          </form>
        </FormProvider>
      </Form>
    </div>
  );
}

// InvoiceUploader — small inline file picker. Uploads to a Supabase Storage bucket
// via the api-server. Defaults to the invoice bucket; pass `uploadFn` to target a
// different bucket (e.g. payment proofs). Surfaces config errors clearly.
export function InvoiceUploader({
  value,
  onChange,
  disabled,
  uploadFn = uploadInvoice,
  label = "Invoice",
}: {
  value: string | null;
  onChange: (url: string | null) => void;
  disabled?: boolean;
  uploadFn?: (file: File) => Promise<UploadResult>;
  label?: string;
}) {
  const { toast } = useToast();
  const [uploading, setUploading] = useState(false);

  async function handleFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const result = await uploadFn(file);
      if (result.ok) {
        onChange(result.url);
        toast({ title: `${label} uploaded.` });
      } else {
        toast({ title: result.message, variant: "destructive" });
      }
    } finally {
      setUploading(false);
      // Reset the input so the same file can be re-selected if needed
      e.target.value = "";
    }
  }

  if (value) {
    // 2026-05-20: show an actual image thumbnail when the upload is an image, so the
    // operator can see it "populated"; PDFs fall back to a labelled link.
    const isImage = /\.(png|jpe?g|webp|gif)(\?|$)/i.test(value);
    return (
      <div className="flex items-center gap-3 rounded-md border bg-muted/30 px-3 py-2">
        {isImage ? (
          <a href={value} target="_blank" rel="noopener noreferrer" className="shrink-0">
            <img src={value} alt="Uploaded invoice" className="h-16 w-16 rounded object-cover border" />
          </a>
        ) : null}
        <a href={value} target="_blank" rel="noopener noreferrer" className="text-sm underline truncate flex-1">
          {isImage ? "View full image" : "View uploaded invoice (PDF)"}
        </a>
        <Button type="button" variant="ghost" size="sm" onClick={() => onChange(null)} disabled={disabled} aria-label="Remove invoice">
          <X className="h-4 w-4" />
        </Button>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2">
      <label className={`flex cursor-pointer items-center gap-2 rounded-md border bg-card px-3 py-2 text-sm hover:bg-muted/50 ${disabled || uploading ? "pointer-events-none opacity-60" : ""}`}>
        {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
        <span>{uploading ? "Uploading…" : "Upload PDF / image"}</span>
        <input
          type="file"
          accept=".pdf,.png,.jpg,.jpeg,.webp,application/pdf,image/*"
          className="sr-only"
          onChange={handleFile}
          disabled={disabled || uploading}
        />
      </label>
      <span className="text-xs text-muted-foreground">Up to 10 MB</span>
    </div>
  );
}

/**
 * Inline Purchase Order picker — shown inside the Stock-In form when
 * transactionType=Purchase. Lists open POs (Placed or Partially_Delivered)
 * with items still outstanding. Selecting one calls onPick(orderId), which
 * the parent uses to close this sheet and open the Record Delivery flow
 * pre-selected to that PO.
 */
function PurchaseOrderPickerInline({ onPick }: { onPick: (orderId: number) => void }) {
  const ordersParams = { pageSize: 200 } as const;
  const { data: ordersResp, isLoading } = useListStockInOrders(ordersParams, {
    query: { queryKey: getListStockInOrdersQueryKey(ordersParams) },
  });
  const openOrders = useMemo(() => {
    const items = ordersResp?.items ?? [];
    return items.filter((o: StockInOrder) => {
      // 2026-05-31: surface ONLY orders that have at least one cart line in
      // the Non Stock-In Delivered state — status='Delivered' but qty not
      // yet covered by a receipt. The picker exists to convert those
      // status flags into formal receipts; everything else (not delivered
      // yet, fully covered, terminal status) stays out.
      if (o.status === "Draft" || o.status === "Cancelled") return false;
      const nonStockIn = Number(
        (o as { nonStockInDeliveredLineCount?: number | null }).nonStockInDeliveredLineCount ?? 0,
      );
      return nonStockIn > 0;
    });
  }, [ordersResp]);

  return (
    <div className="space-y-1.5 rounded-md border bg-blue-50/50 p-3">
      <label className="text-sm font-medium">Purchase Order</label>
      <p className="text-xs text-muted-foreground">
        Pick the PO this purchase belongs to — recording continues in the Record Delivery flow,
        with the PO's open cart lines pre-loaded.
      </p>
      {isLoading ? (
        <p className="text-xs text-muted-foreground py-1">Loading orders…</p>
      ) : openOrders.length === 0 ? (
        <p className="text-xs text-muted-foreground py-1">
          No open POs. Create one from the Orders tab and record payment, then return here.
        </p>
      ) : (
        <Select onValueChange={(v) => onPick(Number(v))}>
          <SelectTrigger className="h-9"><SelectValue placeholder="Choose a PO…" /></SelectTrigger>
          <SelectContent>
            {openOrders.map((o: StockInOrder) => {
              const ref = o.orderId ?? `ORD-${String(o.id).padStart(3, "0")}`;
              const isLegacy = ref.startsWith("LEGACY-");
              const displayId = isLegacy ? ref.replace(/^LEGACY-/, "ORD-") : ref;
              return (
                <SelectItem key={o.id} value={String(o.id)}>
                  <span className="font-mono text-xs">{displayId}</span>
                  {isLegacy && <span className="ml-1.5 rounded bg-amber-100 px-1 py-px text-[10px] font-semibold uppercase tracking-wide text-amber-700">Legacy</span>}
                  {o.vendorNameFreeText ? <> · {o.vendorNameFreeText}</> : null}
                  <span className="ml-2 text-xs text-muted-foreground">{o.status.replace(/_/g, " ")}</span>
                </SelectItem>
              );
            })}
          </SelectContent>
        </Select>
      )}
    </div>
  );
}

